import ResourcesModal from "@/components/resources_modal";
import ResourceList from "@/components/resource_list";
import ShareSvg from "@/components/svgs/share_svg.js";
import { useState, useEffect } from "react";
import stringifySearch from "@/lib/utils";
import Loader from "@/components/loading_animation.js";

const DownloadAction = (props) => {
  const { selectedDownloads, product, errors, setErrors } = props;
  const [loading, setLoading] = useState(false);

  const getIds = () => {
    return Object.values(product.downloads || {})
      .flat()
      .filter((d) => selectedDownloads.includes(d.url))
      .map((d) => d.id);
  };

  const createZip = async () => {
    let data = { file_ids: getIds(), product_id: product.id };
    setLoading(true);

    await fetch(`/lex-api/file_zip/all?${stringifySearch(data)}`, {
      method: "POST",
    }).then(async (resp) => {
      if (resp.ok) {
        setLoading(false);
        let data = await resp.json();
        window.location = data.url;
      } else {
        setLoading(false);
        setErrors(
          "There was a problem completing your download. Please try again later"
        );
      }
    });
  };

  return (
    <>
      {loading ? (
        <Loader>
          <p className="lexcms-wait-message">
            Download is being prepared now, please hold...
          </p>
        </Loader>
      ) : (
        <div className="lexcms-download-zip-wrapper">
          <a
            onClick={selectedDownloads.length > 0 ? createZip : () => {}}
            className={`lexcms-regular-button lexcms-copy-links-button ${
              selectedDownloads.length > 0 ? "" : "disabled"
            }`}
          >
            <ShareSvg />
            <span>Download</span>
          </a>
        </div>
      )}
    </>
  );
};

export default function ShareDownloadsModal({
  product,
  setShowShare,
  showShare,
  type,
  loadsDownloadsAsync,
}) {
  const [productData, setProductData] = useState(product);
  const [loadingDownloads, setLoadingDownloads] = useState(false);

  const [buttonText, setButtonText] = useState("Copy Links");
  const [errors, setErrors] = useState("");
  const [selectedDownloads, setSelectedDownloads] = useState([]);

  const downloadsForKeyCount = (key) => {
    return (productData.downloads || {})[key]?.length;
  };

  const handleChange = (download) => {
    if (selectedDownloads.includes(download.url)) {
      setSelectedDownloads((prev) => prev.filter((d) => d !== download.url));
    } else {
      setSelectedDownloads((prev) => [...prev, download.url]);
    }
  };

  const allSelected = () => {
    return (
      selectedDownloads.length ===
      Object.values(productData.downloads || {}).flat().length
    );
  };

  const selectAll = () => {
    if (allSelected()) {
      setSelectedDownloads([]);
    } else {
      setSelectedDownloads(
        Object.values(productData.downloads || {})
          .flat()
          .map((d) => d.url)
      );
    }
  };

  const copyToClipboard = () => {
    const outputStr = selectedDownloads.join("\r\n");
    navigator.clipboard.writeText(outputStr);
    setButtonText("Copied!");
  };

  const bulkAction =
    type === "download" ? (
      <DownloadAction
        errors={errors}
        setErrors={setErrors}
        selectedDownloads={selectedDownloads}
        product={productData}
      />
    ) : (
      <a
        onClick={selectedDownloads.length > 0 ? copyToClipboard : () => {}}
        className={`lexcms-regular-button lexcms-copy-links-button ${
          selectedDownloads.length > 0 ? "" : "disabled"
        }`}
      >
        <ShareSvg />
        <span>{buttonText}</span>
      </a>
    );

  const shareText =
    type === "download"
      ? `Streamline your downloads! Select one or multiple assets from our resources, zip them into a single file, and download everything you need with just a few clicks.`
      : `Select one or more multiple resource links, copy them with a click, and share them instantly with your team, clients, or collaborators.`;

  useEffect(() => {
    const timer = setTimeout(() => {
      setButtonText("Copy Links");
    }, 2000);

    return () => clearTimeout(timer);
  }, [buttonText]);

  /**
   * Async download loader
   */
  useEffect(() => {
    if (
      loadsDownloadsAsync &&
      showShare &&
      !productData?.downloads &&
      !loadingDownloads
    ) {
      const loadDownloads = async () => {
        try {
          setLoadingDownloads(true);

          const resp = await fetch(`/lex-api/product_show/${product.id}`);

          if (!resp.ok) {
            throw new Error("Failed to load downloads");
          }

          const data = await resp.json();

          setProductData((prev) => ({
            ...prev,
            downloads: data.downloads,
          }));
        } catch (e) {
          console.error(e);
          setErrors("Unable to load downloads.");
        } finally {
          setLoadingDownloads(false);
        }
      };

      loadDownloads();
    }
  }, [showShare, loadsDownloadsAsync]);

  return (
    <ResourcesModal show={showShare} setShow={setShowShare}>
      {type === "download" ? (
        <h3>
          <div className="lexcms-product-downloads-icon"></div>
          <strong>Download</strong>
          {errors && <p className="lexcms-errors">{errors}</p>}
        </h3>
      ) : (
        <h3>
          <div className="lexcms-product-share-icon"></div>
          <strong>Share Links</strong>
        </h3>
      )}

      <hr className="lexcms-section-divider"></hr>

      {loadingDownloads ? (
        <Loader>
          <p className="lexcms-wait-message">Loading resources...</p>
        </Loader>
      ) : (
        <>
          <div className="lexcms-product-top-share-section">
            <p className="lexcms-product-top-share-text">{shareText}</p>

            <div className="lexcms-product-top-share-section-actions">
              <p>
                <input
                  type="checkbox"
                  checked={allSelected()}
                  onChange={selectAll}
                />
                Select All
              </p>

              {bulkAction}
            </div>
          </div>

          {Object.keys(productData.downloads || {}).map((key) => {
            const downloadContainerId = (key) => {
              let categoryId = key 
                .toLowerCase()
                .replace("&", "and")
                .trim()
                .replace(/\s/g, "-");
              return `modal-download-category-${categoryId}`;
            };
            return (
              <div key={key} className="lexcms-product-resource-list-container">
                <div id={downloadContainerId(key)} className="lexcms-product-resource-list-heading">
                  <h5 >{key}</h5>
                  <small>({downloadsForKeyCount(key)})</small>
                </div>

                <hr className="lexcms-section-divider"></hr>

                <ResourceList>
                  {productData.downloads[key]?.map((download, i) => (
                    <li key={i} className="lexcms-product-share-filename">
                      <input
                        type="checkbox"
                        checked={selectedDownloads.includes(download.url)}
                        onChange={() => handleChange(download)}
                      />

                      <a href={download.url} target="_blank">
                        {download.name}
                      </a>
                    </li>
                  ))}
                </ResourceList>
              </div>
            );
          })}
        </>
      )}
    </ResourcesModal>
  );
}
